import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import { deleteStore, exportStores, getStoreList } from 'app/api/store';
import { startExportPoll } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import { ParseTableDate } from 'app/utils/Parsers';
import Toast from 'app/utils/Toast';
import { APP_CONSTANTS, LocalSessionKey } from 'app/constants/app-constants';
import { LocalStorage } from 'app/utils/storage';

const useStoreMangement = () => {
  const [isdeleteFiltering, setIsdeleteFiltering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [values, setValues] = useState({ status: '', isCdit: '' });
  const [appliedFilters, setAppliedFilters] = useState({ status: '', isCdit: '' });

  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const currentPage = 1;
  const dataPerPage = 10;
  const [totalNumber, setTotalNumber] = useState(0);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const response = await exportStores({
        status: appliedFilters.status,
        isCdit: appliedFilters.isCdit,
        mallId: LocalStorage.get(LocalSessionKey.MALL_ID),
      });
      if (response?.responseCode !== 200) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      const jobId = response?.data?.jobId;
      if (!jobId) {
        Toast.error(Message.Error.COMMON_MESSAGE);
        setIsExporting(false);
        return;
      }

      Toast.success('Export started. Preparing your file...');

      startExportPoll(jobId, {
        onDone: (url) => {
          setIsExporting(false);
          window.location.href = url;
          Toast.success('Export ready. File download started.');
        },
        onFailed: (error) => {
          setIsExporting(false);
          Toast.error(error || Message.Error.COMMON_MESSAGE);
        },
        onError: () => {
          setIsExporting(false);
          Toast.error(Message.Error.COMMON_MESSAGE);
        },
      });
    } catch {
      Toast.error(Message.Error.COMMON_MESSAGE);
      setIsExporting(false);
    }
  };

  const sectionObj = {
    sectionTitle: 'Store Mangement',
    searchObj: {
      keys: ['storeName', 'mallName'],
      placeholder: 'Search by Name, Mall name',
      searchType: 'server',
      handleSearch: (e) => {
        console.log('Handle search: ', e);
      },
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: isExporting ? 'Exporting...' : 'Export',
        handleBtnClick: handleExport,
        isDisabled: isExporting,
      },
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen((prev) => !prev),
      },
      {
        imgTrue: true,
        customclassName: '',
        title: 'Add Store',
        handleBtnClick: () => navigate(APP_CONSTANTS.CREATE_STORE),
      },
    ],
  };

  const fetchStoreData = useCallback(
    async (page, search = '', filters = appliedFilters) => {
      setPageLoading(true);
      try {
        const response = await getStoreList({
          page,
          limit: dataPerPage,
          search,
          status: filters.status,
          isCdit: filters.isCdit,
        });
        if (response?.responseCode === 200) {
          const dataArr = response?.data?.stores || [];
          const newArray = dataArr.map((item) => ({
            ...item,
            registerationDate: ParseTableDate(item?.createdAt),
            toggle: item.status,
            mallName: item?.mallName,
            timing: `${item?.storeOpeningTime}-${item?.storeClosingTime}`,
          }));
          setFilteredData(newArray);
          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
          setIsdeleteFiltering(false);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage, appliedFilters]
  );

  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [isFiltering]);

  function handleSubmit(event) {
    event.preventDefault();
    setAppliedFilters(values);
    setIsFilterOpen(false);
    setIsFiltering(true);
  }

  function handleFilterCancel() {
    setIsFilterOpen(false);
  }

  function resetfilter() {
    const empty = { status: '', isCdit: '' };
    setValues(empty);
    setAppliedFilters(empty);
    setIsFiltering(true);
  }

  async function calldeleteStore(data) {
    setPageLoading(true);
    if (data && data?.id) {
      let responseObj = await deleteStore(data?.id);
      setPageLoading(false);
      if (responseObj?.responseCode === 200) {
        setIsdeleteFiltering(true);
        Toast.success('Store Deleted Successfully');
      } else {
        Toast.error(
          responseObj?.response?.message
            ? responseObj?.response?.message
            : Message.Error.COMMON_MESSAGE
        );
      }
    } else {
      Toast.error('Failed to delete');
    }
  }

  function handleDelete(data) {
    confirmAlert({
      title: '',
      message: `Are you sure you want to Delete ${data?.storeName} this store?`,
      buttons: [
        {
          label: 'Yes',
          onClick: () => calldeleteStore(data),
        },
        { label: 'No' },
      ],
      overlayClassName: 'overlay-custom-class-name',
    });
  }

  function handleEdit(data) {
    navigate(`/store/edit/:${data.id}`, { state: { ...data } });
  }

  function handleNew(data) {
    navigate(`/menu/store/:${data.id}`, { state: { ...data } });
  }

  return {
    handleNew,
    handleDelete,
    handleEdit,
    handleExport,
    isExporting,
    pageLoading,
    navigate,
    currentPage,
    dataPerPage,
    pageCount,
    totalNumber,
    filteredData,
    sectionObj,
    fetchStoreData,
    isdeleteFiltering,
    isFilterOpen,
    setIsFilterOpen,
    isFiltering,
    values,
    setValues,
    appliedFilters,
    handleSubmit,
    handleFilterCancel,
    resetfilter,
  };
};

export default useStoreMangement;
